import { NgModule } from '@angular/core';
import { AuthGuard } from '@auth0/auth0-angular';
import { RouterModule, Routes, RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot } from '@angular/router';

import { LandingPageComponent } from './landing-page/landing-page.component';

import { SkipComponent } from './dashboard/skip/skip.component';
import { ReportListComponent } from './dashboard/report-list/report-list.component';
import { SingleReportComponent } from './dashboard/single-report/single-report.component';
import { NoInstanceComponent } from './dashboard/no-instance/no-instance.component';
import { DashboardRedirectComponent } from './dashboard/dashboard-redirect-component';

import { SettingsComponent } from './settings/settings.component';

import { NoAccessComponent } from './shared/no-access/no-access.component';

import { NewOrganizationComponent } from './onboarding/new-organization/new-organization.component';
import { PricingComponent } from './onboarding/pricing/pricing.component';
import { CheckoutComponent } from './onboarding/checkout/checkout.component';
import { ConnectorComponent } from './onboarding/connector/connector.component';
import { WaitingPageComponent } from './onboarding/waiting-page/waiting-page.component';

import { AccessGuard } from './core/guards/access.guard';
import { OrganizationGuard } from './core/guards/organization.guard';
import { SubscriptionGuard } from './core/guards/subscription.guard';
import { PlanDataGuard } from './core/guards/plan-data.guard';
import { OnboardingGuard } from './onboarding/onboarding.guard';
import { InstanceCheckGuard } from './dashboard/instance-check-guard';

import { ProfileSettingsComponent } from './settings/profile/profile.component';
import { OrganizationsSettingsComponent } from './settings/organizations/organizations.component';
import { SecuritySettingsComponent } from './settings/security/security.component';
import { TeamSettingsComponent } from './settings/team/team.component';
import { InstanceListComponent } from './settings/instance-list/instance-list.component';
import { InstanceDetailComponent } from './settings/instance-detail/instance-detail.component';
import { ConnectorsSettingsComponent } from './settings/connector/connector.component';
import { InternalAdminSettingsComponent } from './settings/internal-admin/internal-admin.component';
import { SubscriptionListComponent } from './settings/subscription-list/subscription-list.component';
import { SubscriptionDetailComponent } from './settings/subscription-detail/subscription-detail.component';
import { SubscriptionEditPaymentComponent } from './settings/subscription-edit-payment/subscription-edit-payment.component';
import { SubscriptionUpdateComponent } from './settings/subscription-update/subscription-update.component';
import { RoleAccessGuard } from './core/guards/role-access.guard';

const routes: Routes = [
  { path: '', component: LandingPageComponent, pathMatch: 'full' },
  { path: 'no-access', component: NoAccessComponent },

  {
    path: 'onboarding',
    canActivate: [AuthGuard, AccessGuard, OnboardingGuard],
    canActivateChild: [AuthGuard, AccessGuard, OnboardingGuard],
    children: [
      { path: 'new-organization', component: NewOrganizationComponent }, // Do not require OnboardingGuard here, because we want to allow users to create new organizations when they are already in an organization.
      { path: 'pricing', component: PricingComponent, canActivate: [OrganizationGuard, PlanDataGuard] },
      { path: 'checkout', component: CheckoutComponent, canActivate: [OrganizationGuard, PlanDataGuard] },
      { path: 'connector', component: ConnectorComponent, canActivate: [OrganizationGuard] },
      { path: 'completed', component: WaitingPageComponent, canActivate: [OrganizationGuard] },
    ],
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard, AccessGuard, OrganizationGuard],
    canActivateChild: [AuthGuard, AccessGuard, OrganizationGuard],
    component: DashboardRedirectComponent,
    children: [
      {
        path: 'instance/:instanceId',
        canActivate: [InstanceCheckGuard],
        canActivateChild: [InstanceCheckGuard],
        children: [
          { path: 'chat/:conversationId', component: SkipComponent },
          { path: 'chat', component: SkipComponent },
          { path: 'report-list', component: ReportListComponent },
          { path: 'report-list/:folderId', component: ReportListComponent },
          { path: 'report/:id', component: SingleReportComponent },
        ],
      },
      {
        path: 'no-instance',
        component: NoInstanceComponent,
      }
    ],
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [AuthGuard, AccessGuard, RoleAccessGuard],
    canActivateChild: [AuthGuard, AccessGuard, RoleAccessGuard],
    children: [
      { path: '', redirectTo: 'profile', pathMatch: 'full' },
      { path: 'profile', component: ProfileSettingsComponent },
      { path: 'organizations', component: OrganizationsSettingsComponent, canActivate: [OrganizationGuard] },
      { path: 'security', component: SecuritySettingsComponent, canActivate: [OrganizationGuard] },
      { path: 'team', component: TeamSettingsComponent, canActivate: [OrganizationGuard] },
      { path: 'connectors', component: ConnectorsSettingsComponent, canActivate: [OrganizationGuard, InstanceCheckGuard] },
      { path: 'billing', component: SettingsComponent, canActivate: [OrganizationGuard, InstanceCheckGuard] },
      { path: 'internal-admin', component: InternalAdminSettingsComponent, canActivate: [OrganizationGuard] },
      { path: 'instance-list', component: InstanceListComponent, canActivate: [OrganizationGuard, SubscriptionGuard] },
      { 
        path: 'instance/:instanceId',
        canActivate: [OrganizationGuard],
        canActivateChild: [OrganizationGuard],
        children: [
          { path: '', redirectTo: 'details', pathMatch: 'full' },
          { path: 'details', component: InstanceDetailComponent },
        ]
      },
      { path: 'subscription-list', component: SubscriptionListComponent, canActivate: [OrganizationGuard, SubscriptionGuard, PlanDataGuard] },
      { 
        path: 'subscription/:subscriptionId',
        canActivate: [OrganizationGuard, SubscriptionGuard, PlanDataGuard],
        canActivateChild: [OrganizationGuard, SubscriptionGuard, PlanDataGuard],
        children: [
          { path: '', redirectTo: 'details', pathMatch: 'full' },
          { path: 'details', component: SubscriptionDetailComponent },
          { path: 'update', component: SubscriptionUpdateComponent },
          { path: 'edit-payment', component: SubscriptionEditPaymentComponent }
        ]
      }
    ],
  },

  { path: '**', redirectTo: 'dashboard' },
];



/**
 * Enables route reuse strategy for all routes
 */
export class SkipReuseStrategy implements RouteReuseStrategy {
  private handlers: { [key: string]: DetachedRouteHandle } = {};

  // Determines if a route should be detached for reuse
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return true; // Enable detaching/caching for all routes
  }

  // Stores the detached route
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    this.handlers[this.getRouteKey(route)] = handle;
  }

  // Determines if a route should be reattached
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return !!this.handlers[this.getRouteKey(route)];
  }

  // Retrieves the stored route
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.handlers[this.getRouteKey(route)] || null;
  }

  // Determines if a route should be reused
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    //return future.routeConfig === curr.routeConfig;
    
    // If the route config has changed, do not reuse.
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }

    // Compare the route parameters. If any parameter differs, refresh the route.
    const futureParams = future.params;
    const currParams = curr.params;
    for (const key in futureParams) {
      if (futureParams[key] !== currParams[key]) {
        return false;
      }
    }
    return true;
  }

  // Generate a unique key for each route.
  // For routes under "settings", append ID parameters (subscriptionId and instanceId) to the key.
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    // Check if this route is within the "settings" branch
    const isSettingsRoute = route.pathFromRoot.some(
      r => r.routeConfig && r.routeConfig.path === 'settings'
    );

    return route.pathFromRoot
      .map(r => {
        if (r.routeConfig && r.routeConfig.path) {
          let segment = r.routeConfig.path;
          // Only modify the key if we're in the settings area
          if (isSettingsRoute) {
            // Append the subscriptionId if the segment relates to subscriptions and the parameter exists.
            if (segment.indexOf('subscription') > -1 && r.params && r.params['subscriptionId']) {
              segment += '-' + r.params['subscriptionId'];
            }
            // Append the instanceId if the segment relates to instances and the parameter exists.
            if (segment.indexOf('instance') > -1 && r.params && r.params['instanceId']) {
              segment += '-' + r.params['instanceId'];
            }
          }
          return segment;
        }
        return '';
      })
      .filter(segment => !!segment)
      .join('/');
  }
}


@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [
    { provide: RouteReuseStrategy, useClass: SkipReuseStrategy },
  ],
})
export class AppRoutingModule {}
